import { ErrorHandler, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';

import { IonicModule, IonicRouteStrategy } from '@ionic/angular';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { Drivers } from '@ionic/storage';
import { IonicStorageModule } from '@ionic/storage-angular';
import * as Sentry from '@sentry/capacitor';
import * as SentryAngular from '@sentry/angular';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ApiUrlInterceptor } from './shared/interceptors/api_url.interceptor';
import { CacheInterceptor } from './shared/interceptors/cache.interceptor';
import { JwtInterceptor } from './shared/interceptors/jwt.interceptor';
import { SharedModule } from './shared/shared.module';
import { GlobalErrorHandler } from './shared/services/global-error-handler';
import { VERSION } from './version';
Sentry.init({
  dsn: 'https://0903f9dcdc1848529039a975dff5e0fe@o167801.ingest.sentry.io/6078190',
  // To set your release and dist versions
  release: `trolk_app@${VERSION}`,
  dist: '1',
  // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
  // We recommend adjusting this value in production.
  tracesSampleRate: 0.1,
  integrations: [
    SentryAngular.browserTracingIntegration({
      shouldCreateSpanForRequest: (url) => {
        if (url.includes(environment.urlService)) {
          return false;
        }
        if (url.includes('localhost')) {
          return false;
        }
        return true;
      },
    }),
  ],
  tracePropagationTargets: ['localhost', environment.urlService],
  environment: Capacitor.getPlatform(),
  beforeSend(event, hint) {
    const error = hint.originalException;
    const errorMessage = (error instanceof Error ? error.message : String(error));

      // Filtrar errores del sistema Android que no son accionables
      if (errorMessage.includes('Binder has died') ||
        errorMessage.includes('DeadObjectException')) {
        console.warn('⚠️ Error de sistema Android ignorado:', errorMessage);
        return null;
      }

      // Filtrar errores nativos de iOS no accionables
      if (errorMessage.includes('EXC_CRASH') ||
        errorMessage.includes('SIGSEGV') ||
        errorMessage.includes('WKErrorDomain')) {
        console.warn('⚠️ Error de sistema iOS ignorado:', errorMessage);
        return null;
      }

    // Filtrar errores de instalación de Play Store que son del dispositivo
      if (errorMessage.includes('ERROR_INSTALL_NOT_ALLOWED') ||
        errorMessage.includes('Install Error(-6)') ||
        errorMessage.includes('Install Error(-9)') ||
        errorMessage.includes('ERROR_PLAY_STORE_NOT_FOUND')) {
        console.warn('⚠️ Error de instalación del dispositivo ignorado:', errorMessage);
        return null;
      }

      // Filtrar falso positivo de @capacitor/camera: reader.onloadend
      // dispara con reader.result=null cuando FileReader falla.
      // Bug en CameraWeb._getCameraPhoto() que usa onloadend en vez de onload.
      // Inofensivo: el usuario reintenta la foto y funciona.
      if (errorMessage.includes("Cannot read properties of null (reading 'split')")) {
        const stack = error instanceof Error ? (error.stack || '') : '';
        const isFromCapacitorCamera =
          stack.includes('_getCameraPhoto') ||
          stack.includes('CameraWeb') ||
          stack.includes('@capacitor/camera') ||
          stack.includes('plugin.cjs');

        if (isFromCapacitorCamera) {
          return null;
        }
      }

      // Filtrar timeout de GPS no accionable (condición manejada con toast)
      if (errorMessage.includes('Could not obtain location in time')) {
        console.warn('⚠️ Timeout de GPS ignorado:', errorMessage);
        return null;
      }

      return event;
  },
});

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    IonicModule.forRoot(),
    AppRoutingModule,
    SharedModule,
    IonicStorageModule.forRoot({
      name: '__trolk_app',
      driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode() && Capacitor.getPlatform() === 'web',
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      // Solo en web — en nativo las actualizaciones van por Play Store / App Store.
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: ApiUrlInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
